import React, { createContext, useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';

// Utils
import { useAsyncAction } from './hooks';
import { authApi } from '../api';
import { LoginRequest } from '../models/request';
import { BaseResponse, KeyValue } from '../models/common';
import {
	SESSION_KEYNAME_ANNOUNCEMENT_CHECK,
  STORAGE_REFRESH_TOKEN,
  STORAGE_TOKEN,
  getPreviousRoute,
  saveStringStorage
} from '../services/storage';
import { getCultureBrowser, isNullUndefined } from '../services/utils';

// Redux
import {
  userOpenModalUserChangePasswordAlert,
  userSetCurrent
} from '../store/user';
import { store } from '../store';

interface ILoginFormOptions {
  onSuccess?: () => void;
}

export interface ILoginForm {
  username: string;
  password: string;
  logoutAllDevices?: boolean;
}

export function useLoginForm(options: ILoginFormOptions = {}) {
  const cultureBrowser = getCultureBrowser();
  const { onSuccess } = options;
  const navigate = useNavigate();
  const [serverErrors, setServerErrors] = useState<string[] | null>(null);
  const [hasUserLogged, setHasUserLogged] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const methods = useForm<ILoginForm>();
  const { handleSubmit } = methods;

  async function onSubmitLoginForm(data: ILoginForm) {
    setServerErrors(null);
    setIsLoading(true);
    if (!data.logoutAllDevices) {
      const userIsLoggedIn = await authApi.verifyIfExistsUserLoggedIn(
        data.username
      );
      if (userIsLoggedIn.data.result) {
        setHasUserLogged(true);
        setIsLoading(false);
        return userIsLoggedIn;
      }
    }

    const request: LoginRequest = {
      grant_type: 'password',
      username: data.username,
      password: data.password,
      idCultureBrowser: cultureBrowser
    };
    return authApi
      .loginApi(request)
      .then(response => {
        const { result } = response.data;
        const PREVIOUS_ROUTE_STORAGE = getPreviousRoute();
        const redirectRoute =
          PREVIOUS_ROUTE_STORAGE !== ''
            ? PREVIOUS_ROUTE_STORAGE
            : result.redirectRoute;
        store.dispatch(userSetCurrent(result));

        saveStringStorage(STORAGE_TOKEN, result.token);
        saveStringStorage(STORAGE_REFRESH_TOKEN, result.refreshToken);
				sessionStorage.setItem(SESSION_KEYNAME_ANNOUNCEMENT_CHECK, "false");
        navigate(redirectRoute);

        if (
          !isNullUndefined(result.vizualizeModal) &&
          result.vizualizeModal &&
          !isNullUndefined(result.numberOfDaysToPasswordExpires)
        ) {
          store.dispatch(
            userOpenModalUserChangePasswordAlert(
              true,
              result.numberOfDaysToPasswordExpires
            )
          );
        }
      })
      .catch((error: AxiosError<BaseResponse>) => {
        updateHasUserLogged(false);
        if (error.response && error.response.data) {
          const baseResponseError = error.response.data;
          if (
            baseResponseError.errorMessages &&
            baseResponseError.errorMessages.length > 0
          ) {
            setServerErrors(
              baseResponseError.errorMessages.map((x: KeyValue) => x.value)
            );
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }
  const [submit, submitInProgress] = useAsyncAction(onSubmitLoginForm, [
    authApi.loginApi,
    authApi.verifyIfExistsUserLoggedIn,
    setServerErrors,
    onSuccess,
    hasUserLogged
  ]);

  function updateHasUserLogged(data: boolean) {
    setHasUserLogged(data);
  }

  return {
    submit: handleSubmit(submit),
    submitInProgress: isLoading,
    serverErrors,
    errors: methods.formState.errors,
    register: methods.register,
    watch: methods.watch,
    hasUserLogged,
    updateHasUserLogged,
    methods,
    onSubmitLoginForm
  };
}

// #region Context
type ILoginFormContext = ReturnType<typeof useLoginForm>;

const LoginFormContext = createContext<ILoginFormContext | null>(null);

export const LoginFormProvider: React.FC = ({ children }) => {
  const value = useLoginForm();

  return (
    <LoginFormContext.Provider value={value}>
      {children}
    </LoginFormContext.Provider>
  );
};

export function useLoginFormContext() {
  const context = useContext(LoginFormContext);

  if (!context) {
    throw new Error(
      'useLoginFormContext must be used within a LoginFormProvider'
    );
  }

  return context;
}
// #endregion
